import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { useFragment } from "react-relay";

import { useGetValidCourseAndDurationSelections } from "@/features/BookingParts/hooks/useGetValidCourseAndDurationSelections/useGetValidCourseAndDurationSelections";
import { setDurationAtom } from "@/features/BookingWizard/atoms/actions/setDuration/setDurationAtom";
import { courseIdSelectorAtom } from "@/features/BookingWizard/atoms/selectors/courseIdSelectorAtom/courseIdSelectorAtom";
import { DurationSelectorAtom } from "@/features/BookingWizard/atoms/selectors/durationSelectorAtom/durationSelectorAtom";
import { GroupSizeSelectorAtom } from "@/features/BookingWizard/atoms/selectors/groupSizeSelectorAtom/groupSizeSelectorAtom";
import { currentDurationOptionsAtom } from "@/features/BookingWizard/atoms/stores";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { SelectableTile } from "@whoosh/design-system/src/components/controls/SelectableTile/SelectableTile";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";
import { Box } from "@whoosh/design-system/src/components/primitive/Box/Box";
import { HStack } from "@whoosh/design-system/src/components/primitive/Stack/HStack";

import { CourseDurationOption } from "../types";
import { BucketDurationControlNode$key } from "./__generated__/BucketDurationControlNode.graphql";

export const BucketDurationControl = ({
  nodeRef,
  currentCourseDurationOption,
}: {
  nodeRef: BucketDurationControlNode$key | null;

  currentCourseDurationOption: CourseDurationOption;
}) => {
  const data = useFragment(BucketDurationControlFragmentString, nodeRef);
  const setCurrentDuration = useSetAtom(setDurationAtom);
  const currentDuration = useAtomValue(DurationSelectorAtom);
  const currentCourseId = useAtomValue(courseIdSelectorAtom);
  const setCurrentDurationOptions = useSetAtom(currentDurationOptionsAtom);
  const groupSize = useAtomValue(GroupSizeSelectorAtom);
  const { validDurationOptions, allValidDurationsOptionsMappedByCourseId } =
    useGetValidCourseAndDurationSelections(data ?? null, groupSize ?? 0) ?? [];
  /**
   * We set a map of courseID to duration options to an atom so that we can
   * react to changes to player count / duration that dont' match the current course
   */
  useEffect(() => {
    setCurrentDurationOptions(allValidDurationsOptionsMappedByCourseId);
  }, [allValidDurationsOptionsMappedByCourseId, setCurrentDurationOptions]);

  const selectedCourseValidDurations =
    allValidDurationsOptionsMappedByCourseId
      .find((durationOption) => durationOption.courseId === currentCourseId)
      ?.endSlotOptions.filter((durationOption) =>
        groupSize
          ? durationOption.allowedPlayerCounts.includes(groupSize)
          : true
      )
      .map((durationOption) => durationOption.durationMins) ?? [];

  const defaultDurationOption =
    validDurationOptions.find(
      (durationOption) =>
        !durationOption.isDisabled &&
        selectedCourseValidDurations.includes(durationOption.value)
    ) ??
    validDurationOptions.find((durationOption) => !durationOption.isDisabled);

  const isCurrentDurationValid = !!currentDuration
    ? !!validDurationOptions.find(
        (durationOption) =>
          durationOption.value === currentDuration &&
          !durationOption.isDisabled &&
          (!currentCourseId ||
            selectedCourseValidDurations.includes(durationOption.value))
      )
    : false;

  useEffect(() => {
    if (isCurrentDurationValid || !defaultDurationOption) {
      return;
    }

    setCurrentDuration({ durationMins: defaultDurationOption.value });
  }, [defaultDurationOption, isCurrentDurationValid, setCurrentDuration]);

  return (
    <VStack
      gap={4}
      padding={5}
      paddingBottom={3}
      width="100%"
      backgroundColor="surface-primary"
    >
      <Text variant="mainBaseSemiBold">{t`Duration`}</Text>

      <Box width="100%">
        {validDurationOptions.length === 0 ? (
          <Text variant="mainBaseMedium" color="text-secondary">
            {t`Sorry, there are no valid durations available for this time. Please choose a different time.`}
          </Text>
        ) : validDurationOptions.length > 4 ? (
          <DropdownSelect<number>
            options={validDurationOptions}
            placeholder={t`Select Duration`}
            onChange={(value) => {
              setCurrentDuration({
                durationMins: value,
              });
            }}
            value={currentDuration ?? undefined}
            hideOuterMargin
          />
        ) : (
          <HStack gap={2} flexWrap="wrap">
            {validDurationOptions.map((d) => (
              <SelectableTile
                key={d.value}
                isSmall
                align="center"
                isDisabled={d.isDisabled}
                isSelected={d.value === currentDuration}
                setSelected={(isSelected: boolean) => {
                  if (isSelected && !d.isDisabled) {
                    setCurrentDuration({ durationMins: d.value });
                  }
                }}
                title={d.label as string}
                subtitle={d.subLabel}
              />
            ))}
          </HStack>
        )}
      </Box>
    </VStack>
  );
};

export const BucketDurationControlFragmentString = graphql`
  fragment BucketDurationControlNode on Node {
    ...useGetValidCourseAndDurationSelections_Node
  }
`;
